import { ChevronDown, Filter, Plus, Search, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddCompanySidebar from './AddCompanySidebar';

interface Account {
  id: string;
  name: string;
  primary_department: string;
  industry: string;
  phone: string;
  website: string;
  address: string;
  owner: string;
  createdAt: string;
}

const mockAccounts: Account[] = [
  {
    id: '1',
    name: 'TechTory',
    primary_department: 'Design',
    industry: 'Technology',
    phone: '(218) 374-1230',
    website: 'techtory.com',
    address: '123 Tech Ave',
    owner: 'Sarah Mitchell',
    createdAt: '2023-01-15T14:22:10.223Z'
  },
  {
    id: '2',
    name: 'Smarket',
    primary_department: 'Marketing',
    industry: 'Retail',
    phone: '(123) 091-2808',
    website: 'smarket.com',
    address: '456 Market St',
    owner: 'Marcus Johnson',
    createdAt: '2023-02-20T11:35:15.427Z'
  },
  {
    id: '3',
    name: 'Shopment',
    primary_department: 'Account Manager',
    industry: 'E-commerce',
    phone: '(981) 374-5184',
    website: 'shopment.com',
    address: '789 Shop Ave',
    owner: 'David Rodriguez',
    createdAt: '2023-03-05T09:48:33.112Z'
  },
  {
    id: '4',
    name: 'Grocer Jar',
    primary_department: 'Design',
    industry: 'Food & Beverage',
    phone: '(901) 238-4013',
    website: 'grocerjar.com',
    address: '321 Food St',
    owner: 'Lisa Chen',
    createdAt: '2023-04-17T16:29:44.881Z'
  },
  {
    id: '5',
    name: 'Wholesale Centers',
    primary_department: 'Operations',
    industry: 'Wholesale',
    phone: '(235) 023-4052',
    website: 'wholesalecenters.com',
    address: '654 Wholesale Blvd',
    owner: 'Jayson Oneil',
    createdAt: '2023-05-22T13:11:27.669Z'
  },
  {
    id: '6',
    name: 'Crafery',
    primary_department: 'Marketing',
    industry: 'Arts & Crafts',
    phone: '(102) 493-5891',
    website: 'crafery.com',
    address: '987 Craft Ave',
    owner: 'Emma Thompson',
    createdAt: '2023-06-30T10:15:09.541Z'
  }
];

const Accounts: React.FC = () => {
  const navigate = useNavigate();
  const [accounts] = useState<Account[]>(mockAccounts);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState('All industries');
  const [showIndustryDropdown, setShowIndustryDropdown] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  // Get unique industries from accounts
  const industries = ['All industries', ...Array.from(new Set(accounts.map(account => account.industry)))];

  // Apply search and industry filters
  let filteredAccounts = accounts.filter(account =>
    account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.primary_department.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.owner.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Apply industry filter
  if (selectedIndustry !== 'All industries') {
    filteredAccounts = filteredAccounts.filter(account => account.industry === selectedIndustry);
  }

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowIndustryDropdown(false);
    };

    if (showIndustryDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showIndustryDropdown]);

  const handleSelectAll = () => {
    if (selectedAccounts.length === filteredAccounts.length) {
      setSelectedAccounts([]);
    } else {
      setSelectedAccounts(filteredAccounts.map(account => account.id));
    }
  };

  const handleSelectAccount = (id: string) => {
    if (selectedAccounts.includes(id)) {
      setSelectedAccounts(selectedAccounts.filter(accountId => accountId !== id));
    } else {
      setSelectedAccounts([...selectedAccounts, id]);
    }
  };


  const handleDeleteAccount = (account: Account) => {
    if (window.confirm(`Are you sure you want to delete ${account.name}?`)) {
      // For now, just log since we're using mock data
      console.log('Delete account:', account);
      // TODO: Implement delete functionality for mock accounts
    }
  };


  return (
    <div className="h-full bg-gray-50">
      <div className="flex flex-col overflow-hidden h-full">
        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-white shadow-sm rounded-lg h-full flex flex-col">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-semibold text-gray-900">Accounts</h1>
                <button 
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                >
                  <Plus size={16} />
                  <span>Add Account</span>
                </button>
              </div>

              <div className="flex items-center space-x-4 my-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Search accounts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="relative">
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowIndustryDropdown(!showIndustryDropdown);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    <Filter size={16} />
                    <span>{selectedIndustry}</span>
                    <ChevronDown size={16} />
                  </button>
                  
                  {showIndustryDropdown && (
                    <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-10">
                      <div className="py-1">
                        {industries.map((industry) => (
                          <button
                            key={industry}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedIndustry(industry);
                              setShowIndustryDropdown(false);
                            }}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                              selectedIndustry === industry ? 'bg-teal-50 text-teal-700' : 'text-gray-700'
                            }`}
                          >
                            {industry}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedAccounts.length === filteredAccounts.length && filteredAccounts.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lead Owner
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Primary Department
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone No.
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAccounts.map((account) => (
                    <tr key={account.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/accounts/${account.id}/leads`, { state: { account } })}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedAccounts.includes(account.id)}
                          onChange={() => handleSelectAccount(account.id)}
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-sm">{account.owner}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-sm">{account.name}</div>
                        <div className="text-xs text-gray-500">{account.industry}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {account.primary_department}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {account.phone}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAccount(account);
                            }} 
                            className="text-gray-600 hover:text-red-600"
                            title="Delete account"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <AddCompanySidebar 
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />
    </div>
  );
};

export default Accounts;
